import React, { useEffect, useRef, useState } from "react";
import { createGroupCallSignaling } from "../utils/groupCallSignaling";
import { AUTH_STORAGE_KEY } from "../socket";

export default function GroupVideoCall({ group, onEnd, type = "video" }) {

  const [participants, setParticipants] = useState({});
  const [isMuted,      setIsMuted]      = useState(false);
  const [cameraOff,    setCameraOff]    = useState(false);
  const [error,        setError]        = useState("");

  const localStreamRef  = useRef(null);
  const localVideoRef   = useRef(null);
  const pcsRef          = useRef({});
  const signalingRef    = useRef(null);
  const isEndedRef      = useRef(false);
  const pendingICE      = useRef({});
  const isMutedRef      = useRef(false);
  const cameraOffRef    = useRef(false);
  const audioCtxRef     = useRef(null);
  const callStartedRef  = useRef(false); // ✅ emit group_call_start only once
  const listenersSetRef = useRef(false); // ✅ attach listeners only once

  let currentUser = null;
  try {
    const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    currentUser  = parsed?.user?.email;
  } catch {}

  const ICE_SERVERS = {
    iceServers: [
      { urls: "stun:stun.cloudflare.com:3478" },
      {
        urls: "turn:openrelay.metered.ca:80",
        username: "openrelayproject",
        credential: "openrelayproject"
      },
      {
        urls: "turn:openrelay.metered.ca:443",
        username: "openrelayproject",
        credential: "openrelayproject"
      }
    ]
  };

  // =========================================================
  // PEER CONNECTION
  // =========================================================

  function createPC(remoteUser, signaling, localStream) {
    // Close stale PC if exists
    if (pcsRef.current[remoteUser]) {
      try { pcsRef.current[remoteUser].close(); } catch {}
    }

    const pc = new RTCPeerConnection(ICE_SERVERS);
    pcsRef.current[remoteUser]  = pc;
    pendingICE.current[remoteUser] = [];

    localStream.getTracks().forEach(track =>
      pc.addTrack(track, localStream)
    );

    pc.onicecandidate = (e) => {
      if (e.candidate) signaling.sendICE(remoteUser, e.candidate);
    };

    pc.ontrack = (event) => {
      console.log(`🎥 Track from ${remoteUser}:`, event.track.kind);
      const remoteStream = event.streams[0];
      setParticipants(prev => ({
        ...prev,
        [remoteUser]: { ...prev[remoteUser], stream: remoteStream }
      }));
    };

    pc.onconnectionstatechange = () => {
      console.log(`[${remoteUser}] state:`, pc.connectionState);
      if (
        pc.connectionState === "failed" ||
        pc.connectionState === "disconnected"
      ) {
        removeParticipant(remoteUser);
      }
    };

    return pc;
  }

  function removeParticipant(user) {
    const pc = pcsRef.current[user];
    if (pc) {
      try { pc.close(); } catch {}
      delete pcsRef.current[user];
    }
    delete pendingICE.current[user];
    setParticipants(prev => {
      const next = { ...prev };
      delete next[user];
      return next;
    });
  }

  async function flushICE(remoteUser) {
    const pc = pcsRef.current[remoteUser];
    if (!pc) return;
    for (const c of (pendingICE.current[remoteUser] || [])) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
    }
    pendingICE.current[remoteUser] = [];
  }

  // =========================================================
  // MAIN EFFECT
  // =========================================================

  useEffect(() => {
    const signaling = createGroupCallSignaling(currentUser, group.id);
    signalingRef.current = signaling;
    const { socket } = signaling;

    async function startGroupCall() {
      try {
        const constraints = type === "video"
          ? {
              video: true,
              audio: { echoCancellation: true, noiseSuppression: true }
            }
          : {
              video: false,
              audio: { echoCancellation: true, noiseSuppression: true }
            };

        const localStream = await navigator.mediaDevices
          .getUserMedia(constraints);

        localStreamRef.current = localStream;

        if (localVideoRef.current && type === "video") {
          localVideoRef.current.srcObject = localStream;
        }

        // =========================================================
        // NAMED HANDLERS — defined once, removed on cleanup
        // =========================================================

        const handleUserJoined = async ({ user }) => {
          if (user === currentUser) return;
         
          setParticipants(prev => ({ ...prev, [user]: { stream: null } }));

          const pc    = createPC(user, signaling, localStream);
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          signaling.sendOffer(user, offer, type);
        };

        const handleOffer = async ({ from, offer }) => {
          if (from === currentUser) return;
          console.log(`📞 Offer from ${from}`);

          setParticipants(prev => ({ ...prev, [from]: { stream: null } }));

          const pc = createPC(from, signaling, localStream);
          await pc.setRemoteDescription(new RTCSessionDescription(offer));
          await flushICE(from);

          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          signaling.sendAnswer(from, answer);
        };

        const handleAnswer = async ({ from, answer }) => {
          const pc = pcsRef.current[from];
          if (!pc) return;
          await pc.setRemoteDescription(new RTCSessionDescription(answer));
          await flushICE(from);
        };

        const handleICE = async ({ from, candidate }) => {
          const pc = pcsRef.current[from];
          if (!pc) return;
          if (pc.remoteDescription) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(candidate));
            } catch {}
          } else {
            pendingICE.current[from] = pendingICE.current[from] || [];
            pendingICE.current[from].push(candidate);
          }
        };

        const handleUserLeft = ({ user }) => {
          console.log(`👋 ${user} left`);
          removeParticipant(user);
        };

        // ✅ Attach listeners ONCE
        if (!listenersSetRef.current) {
          listenersSetRef.current = true;

          socket.on("group_call_user_joined", handleUserJoined);
          socket.on("group_call_offer",       handleOffer);
          socket.on("group_call_answer",      handleAnswer);
          socket.on("group_call_ice",         handleICE);
          socket.on("group_call_user_left",   handleUserLeft);

          // Store refs for cleanup
          socket._gcHandlers = {
            handleUserJoined,
            handleOffer,
            handleAnswer,
            handleICE,
            handleUserLeft
          };
        }

        // ✅ Emit group_call_start ONCE
        if (!callStartedRef.current) {
          callStartedRef.current = true;

          socket.emit("group_call_start", {
            groupId:   String(group.id),
            groupName: group.name,
            caller:    currentUser,
            type
          });

          console.log("📣 group_call_start emitted");
        }

        // Wait for receivers to set up, then join the mesh
        await new Promise(resolve => setTimeout(resolve, 600));
        signaling.joinGroupCall(type);

      } catch (err) {
        console.error("Group call error:", err);
        setError("Could not start call: " + err.message);
      }
    }

    startGroupCall();
    return () => endGroupCall();

  }, []); // ✅ empty deps — run once only

  // =========================================================
  // END CALL
  // =========================================================

  function endGroupCall() {
    if (isEndedRef.current) return;
    isEndedRef.current = true;

    console.log("📴 Ending group call");

    // Close all peer connections
    Object.values(pcsRef.current).forEach(pc => {
      try { pc.close(); } catch {}
    });
    pcsRef.current = {};

    // Stop local media
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }

    // Close AudioContext
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
      audioCtxRef.current = null;
    }

    // ✅ Remove all socket listeners cleanly
    const socket = signalingRef.current?.socket;
    if (socket && socket._gcHandlers) {
      const h = socket._gcHandlers;
      socket.off("group_call_user_joined", h.handleUserJoined);
      socket.off("group_call_offer",       h.handleOffer);
      socket.off("group_call_answer",      h.handleAnswer);
      socket.off("group_call_ice",         h.handleICE);
      socket.off("group_call_user_left",   h.handleUserLeft);
      delete socket._gcHandlers;
    }

    signalingRef.current?.leaveGroupCall();
    signalingRef.current?.cleanup();

    if (onEnd) onEnd();
  }

  // =========================================================
  // MUTE / CAMERA
  // =========================================================

  function createSilentTrack() {
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
    }
    const ctx  = new AudioContext();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    const dst  = ctx.createMediaStreamDestination();
    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(dst);
    osc.start();
    audioCtxRef.current = ctx;
    return dst.stream.getAudioTracks()[0];
  }

  function toggleMute() {
    const newMuted = !isMutedRef.current;
    isMutedRef.current = newMuted;
    setIsMuted(newMuted);

    Object.values(pcsRef.current).forEach(pc => {
      const sender = pc.getSenders().find(s => s.track?.kind === "audio");
      if (!sender) return;

      if (newMuted) {
        // ✅ Send silence
        sender.replaceTrack(createSilentTrack()).catch(console.error);
      } else {
        // ✅ Restore mic
        if (audioCtxRef.current) {
          try { audioCtxRef.current.close(); } catch {}
          audioCtxRef.current = null;
        }
        const real = localStreamRef.current?.getAudioTracks()[0];
        if (real) {
          real.enabled = true;
          sender.replaceTrack(real).catch(console.error);
        }
      }
    });
  }

  function toggleCamera() {
    if (type !== "video") return;
    const newOff = !cameraOffRef.current;
    cameraOffRef.current = newOff;
    setCameraOff(newOff);

    const track = localStreamRef.current?.getVideoTracks()[0];
    if (track) {
      track.enabled = !newOff;
      if (localVideoRef.current) {
        localVideoRef.current.style.opacity = newOff ? "0" : "1";
      }
    }
  }

  // =========================================================
  // RENDER
  // =========================================================

  const participantList = Object.entries(participants);

  return (
    <div className="group-call-container">

      {/* Remote participants grid */}
      <div className="group-call-grid">
        {participantList.length === 0 && (
          <div className="group-call-waiting">
            <div style={{ fontSize: 48 }}>
              {type === "video" ? "🎥" : "📞"}
            </div>
            <p>Waiting for others to join...</p>
          </div>
        )}

        {participantList.map(([user, { stream }]) => (
          <div key={user} className="group-call-tile">
            {type === "video" ? (
              <RemoteVideo stream={stream} />
            ) : (
              <div className="voice-tile">
                <div className="voice-avatar">
                  {user.charAt(0).toUpperCase()}
                </div>
              </div>
            )}
            <div className="tile-label">{user}</div>
          </div>
        ))}
      </div>

      {/* Local video preview */}
      {type === "video" && (
        <video
          ref={localVideoRef}
          autoPlay muted playsInline
          className="group-local-video"
        />
      )}

      {/* Controls */}
      <div className="call-controls">
        <button className="call-btn" onClick={toggleMute}>
          {isMuted ? "🔊 Unmute" : "🔇 Mute"}
        </button>

        {type === "video" && (
          <button className="call-btn" onClick={toggleCamera}>
            {cameraOff ? "📷 Camera On" : "📷 Camera Off"}
          </button>
        )}

        <button className="call-btn btn-end" onClick={endGroupCall}>
          Leave Call
        </button>
      </div>

      <div className="group-call-info">
        {group.name} • {participantList.length + 1} participant(s)
      </div>

      {error && (
        <div style={{
          position: "absolute", bottom: 80,
          left: "50%", transform: "translateX(-50%)",
          background: "#ef4444", color: "#fff",
          padding: "8px 16px", borderRadius: 6
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ✅ Separate component — attaches stream via ref to avoid srcObject timing issues
function RemoteVideo({ stream }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
      ref.current.play().catch(() => {});
    }
  }, [stream]);

  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      className="group-remote-video"
    />
  );
}